import React, { useState, useEffect, useCallback } from 'react';
import { tagsApi } from '../../services/api';
import { formatDate } from './formatDate';
import { Tag, Plus, Trash2, AlertCircle, Loader2 } from 'lucide-react';

const TAG_COLORS = [
  'bg-blue-100 text-blue-700',
  'bg-purple-100 text-purple-700',
  'bg-green-100 text-green-700',
  'bg-amber-100 text-amber-700',
  'bg-rose-100 text-rose-700',
  'bg-teal-100 text-teal-700',
  'bg-indigo-100 text-indigo-700',
  'bg-orange-100 text-orange-700',
];

function tagColor(idx) {
  return TAG_COLORS[idx % TAG_COLORS.length];
}

function usageCount(tag) {
  return Number(tag.policy_count || 0) + Number(tag.video_count || 0);
}

function TagManagement({ onTagsChanged }) {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newTagInput, setNewTagInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const fetchId = React.useRef(0);

  const fetchTags = useCallback(async () => {
    const id = ++fetchId.current;
    try {
      setError(null);
      const data = await tagsApi.getAll();
      if (id !== fetchId.current) return;
      if (!Array.isArray(data) || data.some(tag => !tag.id || typeof tag.name !== 'string' ||
          tag.policy_count == null || tag.video_count == null)) {
        throw new Error('Invalid resource tab data from server');
      }
      setTags(data);
      onTagsChanged?.(data);
      return data;
    } catch (err) {
      if (id === fetchId.current) setError(err.message);
    } finally {
      if (id === fetchId.current) setLoading(false);
    }
  }, [onTagsChanged]);

  useEffect(() => {
    fetchTags();
    return () => { fetchId.current += 1; };
  }, [fetchTags]);

  async function handleAdd() {
    const name = newTagInput.trim();
    if (!name) return;
    setSubmitting(true);
    try {
      await tagsApi.create(name);
      setNewTagInput('');
      await fetchTags();
    } catch (err) {
      alert('Failed to create resource tab: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete(id) {
    const tag = tags.find(item => item.id === id);
    if (tag && usageCount(tag) > 0) {
      alert('This resource tab is assigned to a document or video and cannot be deleted.');
      return;
    }

    if (!window.confirm('Delete this resource tab?')) return;
    try {
      await tagsApi.delete(id);
      await fetchTags();
    } catch (err) {
      alert('Failed to delete resource tab: ' + err.message);
    }
  }

  if (loading) {
    return (
      <div className="animate-pulse space-y-3">
        <div className="h-8 bg-gray-200 rounded w-40" />
        {[1, 2, 3].map(i => <div key={i} className="h-12 bg-gray-100 rounded-xl" />)}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-16 h-16 rounded-2xl bg-red-100 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Failed to load resource tabs</h2>
          <p className="text-gray-500 mb-6">{error}</p>
          <button onClick={fetchTags} className="px-6 py-3 bg-[#00AAFF] text-white font-medium rounded-xl hover:bg-[#0088cc] transition-colors">Try Again</button>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#00AAFF]/10 flex items-center justify-center">
            <Tag className="w-5 h-5 text-[#00AAFF]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Resource Tab Management</h1>
            <p className="text-sm text-gray-500">{tags.length} resource tab{tags.length !== 1 ? 's' : ''} available</p>
          </div>
        </div>
      </div>

      {/* Add tag bar */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 mb-6">
        <p className="text-sm font-medium text-gray-700 mb-3">Create a new resource tab</p>
        <div className="flex gap-3">
          <input
            type="text"
            value={newTagInput}
            onChange={e => setNewTagInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAdd(); } }}
            placeholder="Resource tab name…"
            className="flex-1 px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#00AAFF] focus:ring-1 focus:ring-[#00AAFF] transition-all"
          />
          <button
            onClick={handleAdd}
            disabled={submitting || !newTagInput.trim()}
            className="flex items-center gap-2 px-5 py-2.5 bg-[#00AAFF] hover:bg-[#0088cc] text-white text-sm font-medium rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Add Resource Tab
          </button>
        </div>
      </div>

      {/* Tags table */}
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
        {tags.length === 0 ? (
          <div className="py-16 text-center">
            <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-4">
              <Tag className="w-7 h-7 text-gray-400" />
            </div>
            <p className="text-sm text-gray-500">No resource tabs yet. Create your first resource tab above.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left text-xs font-medium text-gray-400 uppercase tracking-wider px-4 py-3">Resource Tab</th>
                <th className="text-left text-xs font-medium text-gray-400 uppercase tracking-wider px-4 py-3">Usage</th>
                <th className="text-left text-xs font-medium text-gray-400 uppercase tracking-wider px-4 py-3">Created</th>
                <th className="text-right text-xs font-medium text-gray-400 uppercase tracking-wider px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {tags.map((tag, idx) => (
                <tr key={tag.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${tagColor(idx)}`}>
                      <Tag className="w-3 h-3" />
                      {tag.name}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500">
                    {usageCount(tag) > 0 ? (
                      <span>
                        {Number(tag.policy_count || 0)} document{Number(tag.policy_count || 0) === 1 ? '' : 's'}
                        {' / '}
                        {Number(tag.video_count || 0)} video{Number(tag.video_count || 0) === 1 ? '' : 's'}
                      </span>
                    ) : (
                      <span className="text-gray-300">Unused</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-400">
                    {formatDate(tag.created_at)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end">
                      <button
                        onClick={() => handleDelete(tag.id)}
                        disabled={usageCount(tag) > 0}
                        className="p-1.5 hover:bg-red-100 rounded-md transition-colors disabled:opacity-35 disabled:hover:bg-transparent disabled:cursor-not-allowed"
                        title={usageCount(tag) > 0 ? 'Remove this resource tab from all documents and videos before deleting it' : 'Delete resource tab'}
                      >
                        <Trash2 className="w-3.5 h-3.5 text-red-400" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

    </div>
  );
}

export default TagManagement;
